import { useEffect, useId, useMemo, useState } from 'react';
import { Alert, Button, Modal } from '@/components/ui';
import { SimpleInput } from '@/components/ui/SimpleInput';
import { SimpleSelect } from '@/components/ui/SimpleSelect';
import { SimpleSwitch } from '@/components/ui/SimpleSwitch';
import { SimpleTextarea } from '@/components/ui/SimpleTextarea';
import { ReportsService } from '@/services/reports.service';
import { getApiErrorStatus, getWriteErrorMessage } from '@/utils/apiError';
import type {
  FileReportCategoryOption,
  FileReportFormValues,
  ManagedFileReport,
} from '@/types/reports.types';
import { FileReportFilePicker } from './FileReportFilePicker';
import {
  KEY_ENTITLEMENT_CATEGORY,
  KEY_ENTITLEMENT_WARNING,
  MAX_ANNOTATION_LENGTH,
  MAX_CAPTION_LENGTH,
  toDateInputValue,
  validateFile,
} from './fileReportRules';

interface FileReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The report to edit; leave out to add a new one. */
  report?: ManagedFileReport | null;
  categoryOptions: FileReportCategoryOption[];
  /** Category (PageTypeName) to preselect when adding, e.g. on Pool Point Calculation. */
  defaultCategory?: string;
  onSaved: (operation: 'created' | 'updated', caption: string) => void;
}

interface FormState {
  pageCaption: string;
  categoryId: string;
  pageDate: string;
  pagePriority: string;
  reportAnnotation: string;
  visible: boolean;
}

type FormErrors = Partial<Record<keyof FormState | 'file', string>>;

/**
 * Add a file report, or edit an existing one's details (the file itself is changed with
 * ReplaceFileDialog). Mirrors the legacy Admin/EditCustomPage.aspx form.
 */
export function FileReportDialog({
  isOpen,
  onClose,
  report,
  categoryOptions,
  defaultCategory,
  onSaved,
}: FileReportDialogProps) {
  const formId = useId();
  const isEditing = !!report;
  const [form, setForm] = useState<FormState>(emptyForm());
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A report can sit in a category that has since been hidden; keep it selectable when editing.
  const options = useMemo(() => {
    if (!report || categoryOptions.some((o) => o.id === report.categoryId)) return categoryOptions;
    return [
      ...categoryOptions,
      {
        id: report.categoryId,
        pageTypeName: report.fileCategory,
        label: report.fileCategory,
        pools: [],
      },
    ];
  }, [categoryOptions, report]);

  useEffect(() => {
    if (!isOpen) return;
    if (report) {
      setForm({
        pageCaption: report.fileCaption,
        categoryId: report.categoryId.toString(),
        pageDate: report.fileDate.slice(0, 10),
        pagePriority: report.priority.toString(),
        reportAnnotation: report.annotation ?? '',
        visible: report.visible,
      });
    } else {
      const preselected = categoryOptions.find((o) => o.pageTypeName === defaultCategory);
      setForm({ ...emptyForm(), categoryId: preselected ? preselected.id.toString() : '' });
    }
    setFile(null);
    setErrors({});
    setSubmitError(null);
    // Only reset when the dialog opens or switches report, not when options load in the background.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, report]);

  const selectedCategory = options.find((o) => o.id.toString() === form.categoryId);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): FormErrors => {
    const next: FormErrors = {};
    const caption = form.pageCaption.trim();
    if (!caption) next.pageCaption = 'Enter a caption.';
    else if (caption.length > MAX_CAPTION_LENGTH)
      next.pageCaption = `The caption must be at most ${MAX_CAPTION_LENGTH} characters.`;
    if (!form.categoryId) next.categoryId = 'Choose a category.';
    if (!form.pageDate) next.pageDate = 'Choose the report date.';
    if (!/^-?\d+$/.test(form.pagePriority.trim()))
      next.pagePriority = 'Enter a whole number (1 is the default).';
    if (form.reportAnnotation.trim().length > MAX_ANNOTATION_LENGTH)
      next.reportAnnotation = `The annotation must be at most ${MAX_ANNOTATION_LENGTH} characters.`;
    if (!isEditing) {
      const fileError = validateFile(file);
      if (fileError) next.file = fileError;
    }
    return next;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const values: FileReportFormValues = {
      pageCaption: form.pageCaption.trim(),
      categoryId: Number(form.categoryId),
      pageDate: form.pageDate,
      pagePriority: Number(form.pagePriority.trim()),
      reportAnnotation: form.reportAnnotation.trim(),
      visible: form.visible,
    };

    setSaving(true);
    setSubmitError(null);
    try {
      if (report) await ReportsService.updateFileReport(report.pageId, values);
      else await ReportsService.createFileReport(values, file!);
      onSaved(report ? 'updated' : 'created', values.pageCaption);
      onClose();
    } catch (error) {
      const message = getWriteErrorMessage(error, 'The file report could not be saved.');
      if (getApiErrorStatus(error) === 409)
        setErrors((prev) => ({ ...prev, pageCaption: message }));
      else setSubmitError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={isEditing ? 'Edit file report' : 'Add file report'}
      size="lg"
      showCloseButton
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form={formId} disabled={saving} className="min-w-24">
            {saving ? (isEditing ? 'Saving…' : 'Uploading…') : isEditing ? 'Save' : 'Add file'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-5">
        {submitError && <Alert variant="error">{submitError}</Alert>}

        <SimpleInput
          label="Caption"
          required
          value={form.pageCaption}
          maxLength={MAX_CAPTION_LENGTH}
          onChange={(e) => setField('pageCaption', e.target.value)}
          error={errors.pageCaption}
          helperText="Shown as the file's title. Must be unique."
          disabled={saving}
        />

        <div>
          <SimpleSelect
            label="Category"
            required
            value={form.categoryId}
            onChange={(e) => setField('categoryId', e.target.value)}
            placeholder="Choose a category"
            options={[
              { value: '', label: 'Choose a category' },
              ...options.map((o) => ({ value: o.id.toString(), label: o.label })),
            ]}
            error={errors.categoryId}
            disabled={saving}
          />
          {selectedCategory && <AudienceHint category={selectedCategory} />}
        </div>

        {selectedCategory?.pageTypeName === KEY_ENTITLEMENT_CATEGORY && (
          <Alert variant="warning">{KEY_ENTITLEMENT_WARNING}</Alert>
        )}

        <div className="grid gap-5 sm:grid-cols-2">
          <SimpleInput
            label="Report date"
            type="date"
            required
            value={form.pageDate}
            onChange={(e) => setField('pageDate', e.target.value)}
            error={errors.pageDate}
            disabled={saving}
          />
          <SimpleInput
            label="Priority"
            type="number"
            inputMode="numeric"
            step={1}
            required
            value={form.pagePriority}
            onChange={(e) => setField('pagePriority', e.target.value)}
            error={errors.pagePriority}
            disabled={saving}
          />
        </div>

        <SimpleTextarea
          label="Annotation"
          rows={3}
          value={form.reportAnnotation}
          maxLength={MAX_ANNOTATION_LENGTH}
          onChange={(e) => setField('reportAnnotation', e.target.value)}
          error={errors.reportAnnotation}
          helperText={`${form.reportAnnotation.length} / ${MAX_ANNOTATION_LENGTH}`}
          disabled={saving}
        />

        <SimpleSwitch
          checked={form.visible}
          onChange={(visible) => setField('visible', visible)}
          label="Visible"
          description="Hidden files are only shown to file report admins."
          disabled={saving}
        />

        {report ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            File:{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">{report.fileName}</span>
            {' · '}use Replace file in the file menu to change it.
            <br />
            Last updated {formatDateTime(report.updatedDate)}
            {report.updatedBy ? ` by ${report.updatedBy}` : ''}.
          </p>
        ) : (
          <FileReportFilePicker
            file={file}
            onChange={(next) => {
              setFile(next);
              setErrors((prev) => ({
                ...prev,
                file: next ? (validateFile(next) ?? undefined) : undefined,
              }));
            }}
            error={errors.file}
            disabled={saving}
          />
        )}
      </form>
    </Modal>
  );
}

/** Who will see files in the chosen category (TIX.PoolPageTypeName; no rows = no restriction). */
function AudienceHint({ category }: { category: FileReportCategoryOption }) {
  if (category.pools.length === 0) {
    return (
      <p className="mt-1.5 text-sm text-amber-700 dark:text-amber-400">
        Not restricted to any pool: every customer with File Reports access will see this file.
      </p>
    );
  }
  return (
    <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
      Visible to companies in: {category.pools.join(', ')}.
    </p>
  );
}

function emptyForm(): FormState {
  return {
    pageCaption: '',
    categoryId: '',
    pageDate: toDateInputValue(new Date()),
    pagePriority: '1',
    reportAnnotation: '',
    visible: true,
  };
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
